import {
  DisplayAdVariantPackFieldsFragment,
  ItemsContext_OrderGuideItemFieldsFragment,
  ItemsContext_VariantsPackFieldsFragment,
  OrderGuideSortOrder,
  PaidSearchEventData,
  SampleRequestVariantPackFieldsFragment,
  VariantPackInfoContext_GroupBadgeFieldsFragment,
  VariantPackOrderCount,
} from '@/types';
import { ListOption } from '@/components/list-item';
import { ListSection } from '@/components/recycler-list';
import {
  PackUnit,
  VariantPackAvailabilityStatus,
  VariantPackGroupType,
} from '@/enums';
import { VariantPackVolumePromotion } from '@/utils/volume-promotions-types';

export interface FilteredCategoryItemGroup {
  directSubcategories?: FilteredCategoryItemGroup[];
  name: string;
  path: string;
  type?: VariantPackGroupType | null;
  uuid?: string | null;
}

/**
 * ItemSortOrder extends backend OrderGuideSortOrder with frontend-only values.
 *
 * - OrderGuideSortOrder (6 values): Backend API type, used by getParSheetPdf
 * - ItemSortOrder (8 values): Frontend type, includes ORDER_FREQUENCY for Hasura queries
 *
 * By extending OrderGuideSortOrder, its values are automatically compatible with ItemSortOrder.
 * This means OrderGuideSortOrder can be used wherever ItemSortOrder is expected.
 */
export const ItemSortOrder = {
  ...OrderGuideSortOrder,
  OrderFrequency: 'ORDER_FREQUENCY',
  OrderFrequencyReverse: 'ORDER_FREQUENCY_REVERSE',
} as const;

export type ItemSortOrder = (typeof ItemSortOrder)[keyof typeof ItemSortOrder];

export interface VariantPackMetadata {
  additional_info?: {
    case_weight?: number;
    demo_recommended_variant_pack_ids?: string[];
    demo_related_variant_pack_ids?: string[];
    gtin_14?: string;
    maximum?: string;
    minimum?: string;
    palletsize?: number;
  };
  demo_recommended_variant_pack_ids?: string[];
  demo_related_variant_pack_ids?: string[];
}

export interface PrebookConfig {
  estimated_fulfillment_at: string;
  order_from: string;
  order_until: string;
}

export interface ItemListOptionMetadata {
  availabilityStatus: VariantPackAvailabilityStatus | null;
  availableQuantity: number | null;
  badges?: VariantPackInfoContext_GroupBadgeFieldsFragment[];
  baseUnit: PackUnit | null;
  catalogSearchResult?: boolean;
  category: string | null;
  currencyCode: string | null;
  description?: string | null;
  displayAd?: DisplayAdVariantPackFieldsFragment;
  displayName: string;
  earliestFulfillmentAt?: string | null;
  externalID: string | null;
  extraKeywords?: string | null;
  itemRecommendationUUID?: string;
  itemUUID: string;
  leadTimeCutoffTime?: string | null;
  leadTimeDays?: number | null;
  maximumQuantity: number | null;
  minimumQuantity: number | null;
  modifierGroups?: VariantPackModifierGroup[];
  modifierString?: string;
  notes?: string | null;
  orderDate?: string | null;
  orderGuidePartitionUUID?: string;
  // Stores a set of nested variantPackUUIDs that are out of stock in order to
  // make the check if any statuses have changed more performant
  outOfStockUUIDs: Set<string>;
  packDisplayString: string;
  packUnit: PackUnit;
  paidSearch?: PaidSearchEventData;
  photoURLs: string[];
  prebookConfig?: PrebookConfig | null;
  promotion: VariantPackVolumePromotion | null;
  promotionPricePerUnitInMicros: number | null;
  sampleRequest?: SampleRequestVariantPackFieldsFragment;
  skus: ItemListOptionMetadata[];
  unitCount: number;
  unitPriceInMicros?: number | null;
  upcList: string[] | null;
  variantPackGroupCategoryUUIDs?: string[];
  variantPackMetadata: VariantPackMetadata;
  variantPackOrderCounts?: VariantPackOrderCount[];
  variantPackUUID: string;
}

export interface ModifierOption {
  modifierName: string;
  modifierSortOrder: number | null;
  modifierUUID: string;
}

export interface VariantPackModifierGroup {
  groupName: string;
  groupSortOrder: number | null;
  groupUUID: string;
  option: ModifierOption;
}

export interface PackModifierDisplayMap {
  [packDisplayString: string]: {
    groupName: string;
    groupUUID: string;
    options: ModifierOption[];
  }[];
}

export interface PackModifierHashMap {
  [packDisplayString: string]: {
    [hash: number]: {
      baseUnit: PackUnit | null;
      currencyCode: string | null;
      externalID: string | null;
      promotionPricePerUnitInMicros: number | null;
      unitCount: number;
      unitPriceInMicros: number | null;
      variantPackUUID: string;
    };
  };
}

export interface AvailabilityMap {
  [variantPackUUID: string]: number;
}

export enum ContainmentLevel {
  Contains = 'CONTAINS',
  FreeFrom = 'FREE_FROM',
  MayContain = 'MAY_CONTAIN',
}

export interface Allergen {
  containment_level: ContainmentLevel;
  name: string;
}

export interface NutritionalInfoNutrient {
  amount: string;
  percent_dv: number | null;
  unit_name: string | null;
}

export interface NutritionalInfo {
  added_sugars: NutritionalInfoNutrient | null;
  calcium: NutritionalInfoNutrient;
  calories: string;
  cholesterol: NutritionalInfoNutrient;
  dietary_fiber: NutritionalInfoNutrient;
  iron: NutritionalInfoNutrient;
  potassium: NutritionalInfoNutrient;
  protein: NutritionalInfoNutrient;
  saturated_fat: NutritionalInfoNutrient;
  serving_size: string;
  servings_per_container: string;
  sodium: NutritionalInfoNutrient;
  total_carbohydrate: NutritionalInfoNutrient;
  total_fat: NutritionalInfoNutrient;
  total_sugars: NutritionalInfoNutrient;
  trans_fat: NutritionalInfoNutrient;
  vitamin_a: NutritionalInfoNutrient | null;
  vitamin_c: NutritionalInfoNutrient | null;
  vitamin_d: NutritionalInfoNutrient;
  vitamin_e: NutritionalInfoNutrient | null;
}

export interface GTINMetadata {
  allergens?: Allergen[];
  brand_name?: string;
  category?: string;
  child_nutrition?: boolean;
  description?: string;
  gross_weight?: string;
  ingredients?: string;
  item_id?: string;
  item_image_urls?: string[];
  item_name?: string;
  kosher?: boolean;
  long_name?: string;
  manufacturer?: string;
  manufacturer_product_code?: string;
  net_weight?: string;
  nutritional_info?: NutritionalInfo;
  origin_country?: string;
  original_item_image_urls?: string[];
  pack_size?: string;
  pack_unit_quantity?: string;
  sell_sheet_pdf_url?: string;
  shelf_life_days?: string;
  ship_height?: string;
  ship_length?: string;
  ship_ti_hi?: string;
  ship_volume?: string;
  ship_width?: string;
  short_name?: string;
  storage_temperature?: string;
}

export type ItemListOption = ListOption<ItemListOptionMetadata>;

export type ItemListOptionSection = ListSection<ItemListOption>;

export interface VariantPackPartitionMap {
  [variantPackUUID: string]: {
    [partitionUUID: string]: ItemsContext_OrderGuideItemFieldsFragment;
  };
}

export interface CatalogCategoryType {
  imageUrl?: string | null;
  name: SupplierCatalogCategoryType;
  uuid?: string;
}

export type SupplierCatalogCategoryType = string | null;

export interface OrderGuideProviderProps {
  children: React.ReactNode;
}

export type PartitionFilterType = ListOption<{ isDefault: boolean }>;

export interface OrderGuidePartition {
  isDefault: boolean;
  name: string;
  uuid: string;
}

export type OrderGuideCollection = {
  defaultPartitionUUID: string | null;
  isDefault: boolean;
  isLocked: boolean;
  name: string;
  partitions: OrderGuidePartition[];
  uuid: string;
} & (
  | { linkedSupplierCollectionUUID?: string | null; type: 'restaurant' }
  | { linkedSupplierCollectionUUID?: never; type: 'supplier' }
);

export interface VariantPackGroup {
  badges: VariantPackInfoContext_GroupBadgeFieldsFragment[];
  directSubgroupUUIDs: string[];
  indirectSubgroupUUIDs: string[];
  name: string;
  type: VariantPackGroupType | null;
  uuid: string;
}

export interface OrderGuideItemsContextType {
  allVariantPacks: ItemsContext_VariantsPackFieldsFragment[];
  categories: string[];
  categoryFilter: string | null;
  categoryItemGroupFilter: FilteredCategoryItemGroup | null;
  categoryItemGroups: VariantPackGroup[];
  collections: OrderGuideCollection[];
  currencyCode: string;
  hasResponse: boolean;
  isInOrderGuide: (variantPackUUID: string) => boolean;
  loading: boolean;
  partitionFilter: PartitionFilterType | null;
  partitionFilters: PartitionFilterType[];
  partitionToCollectionMap: Map<string, string>;
  refetch: () => Promise<void>;
  sections: ItemListOptionSection[];
  selectedCollection: OrderGuideCollection | null;
  setCategoryFilter: React.Dispatch<React.SetStateAction<string | null>>;
  setCategoryItemGroupFilter: React.Dispatch<
    React.SetStateAction<FilteredCategoryItemGroup | null>
  >;
  setPartitionFilter: React.Dispatch<
    React.SetStateAction<PartitionFilterType | null>
  >;
  variantPackPartitionMap: VariantPackPartitionMap;
}
